/**
 * Phase2RosterGrid.tsx
 * EXPERIMENTAL "Roster lane" presentation (3rd toggle) for the Phase-2 task schedule.
 *
 * Each task is ONE horizontal lane. A booking shows as a bar spanning the weeks it runs
 * (so a multi-week test reads as one continuous bar instead of N cards). Staffed people
 * ride on the bar as photo avatars. Wave 3 (percent retirement): the % chips and the
 * drag-bar edit popover are GONE — staffing is presence; click an avatar → a popover
 * with the person + remove (with confirmation). The lane keeps the shared week-column
 * geometry, so it lines up with the capacity grid.
 *
 * This view operates at the BOOKING level (bulk across weeks), unlike the per-week
 * Classic/Board cells — so it has its own light handlers rather than useWeeklyCellModel.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Box, IconButton, Popover, Snackbar, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { alpha, useTheme } from '@mui/material/styles';

import { InlineAssigneePicker } from '../../tasks/InlineAssigneePicker';
import { InitialsAvatar } from '../../../../components/schedule/InitialsAvatar';
import { BookingWeekStrip } from './BookingWeekStrip';
import { taskWorkLabel } from '../../../../utils/bookingLabel';
import { useFlashUser } from './flashUserContext';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../../../components/shared/ConfirmDialog';
import { AssignEmployeesDialog } from '../../tasks/AssignEmployeesDialog';
import { iconForTaskType } from './testTypeIcons';
import { displayTaskName } from './taskDisplayName';
import { deleteEmployeeAssignment } from '../../../../services/scheduleAssignments';
import { weekStartsInRange, weekOverlapsRange, fmtShortDate } from '../../../../utils/weekUtils';
import { useGraphPhotos } from '../../../../hooks/useGraphPhotos';
import { RAIL_GUTTER_W, RAIL_WEEK_W } from './phase2RailGeometry';
import type { Phase2WeeklyGridProps } from './Phase2WeeklyGrid';
import type { TaskWithBookings, BookingView, TaskAssignee } from '../../../../services/scheduleTasks';
import type { AssignableUser } from '../../../../services/scheduleAssignments';

type Props = Phase2WeeklyGridProps;

const BAR_ROW_H = 58;

/** First/last week-column index a booking overlaps (null = outside the window). */
function bookingSpan(b: BookingView, weekColumns: string[]): { startIdx: number; endIdx: number } | null {
  let startIdx = -1;
  let endIdx = -1;
  weekColumns.forEach((ws, i) => {
    if (weekOverlapsRange(ws, b.StartDate, b.EndDate)) {
      if (startIdx === -1) startIdx = i;
      endIdx = i;
    }
  });
  return startIdx === -1 ? null : { startIdx, endIdx };
}

/** One person on a booking: their rows across the booking's weeks (for bulk remove). */
interface RosterPerson {
  userKey: string;
  displayName: string;
  email: string;
  status: 'Tentative' | 'Committed';
  rowKeys: string[];     // every EmployeeAssignmentKey for this booking × user
}

/** Dedupe assignment rows to one person each (collecting every row key for bulk remove). */
function groupByUser(rows: TaskAssignee[]): RosterPerson[] {
  const byUser = new Map<string, RosterPerson>();
  for (const r of rows) {
    const p = byUser.get(r.UserKey);
    if (p) {
      p.rowKeys.push(r.EmployeeAssignmentKey);
    } else {
      byUser.set(r.UserKey, {
        userKey: r.UserKey, displayName: r.displayName, email: r.email,
        status: r.Status === 'Committed' ? 'Committed' : 'Tentative',
        rowKeys: [r.EmployeeAssignmentKey],
      });
    }
  }
  return Array.from(byUser.values());
}

/**
 * People on a booking. `includeNull` folds legacy task-level rows (MachineBookingKey
 * = null) into the sole booking — exactly what useWeeklyCellModel does so the Board
 * and Roster show the SAME set (otherwise null-booking rows silently vanished here).
 */
function peopleOnBooking(rows: TaskAssignee[], bookingKey: string, includeNull: boolean): RosterPerson[] {
  return groupByUser(rows.filter((r) => {
    const mbk = r.MachineBookingKey ?? null;
    return mbk === bookingKey || (includeNull && mbk === null);
  }));
}

function computeWeekColumns(tasks: TaskWithBookings[]): string[] {
  let minStart: string | null = null;
  let maxEnd: string | null = null;
  for (const t of tasks) {
    if (t.StartDate && (!minStart || t.StartDate < minStart)) minStart = t.StartDate;
    if (t.EndDate && (!maxEnd || t.EndDate > maxEnd)) maxEnd = t.EndDate;
  }
  return minStart && maxEnd ? weekStartsInRange(minStart, maxEnd) : [];
}

export const Phase2RosterGrid: React.FC<Props> = ({
  tasks,
  assignmentMap,
  scenarioKey,
  onAssignmentsChanged,
  onAllocationChanged,
  hoveredWeek,
  onHoverWeek,
  holidays,
}) => {
  const theme = useTheme();
  const flashUser = useFlashUser();
  const weekColumns = computeWeekColumns(tasks);
  const trackW = weekColumns.length * RAIL_WEEK_W;

  // Real Graph profile photos for the bar avatars (best-effort, cached).
  const photoByEmail = useGraphPhotos(useMemo(() => {
    const set = new Set<string>();
    for (const rows of Object.values(assignmentMap)) for (const a of rows) if (a.email) set.add(a.email);
    return Array.from(set);
  }, [assignmentMap]));

  // ── Edit popover (click an avatar) ────────────────────────────────────────
  const [edit, setEdit] = useState<{ anchorEl: HTMLElement; task: TaskWithBookings; bookingKey: string; person: RosterPerson } | null>(null);
  // ── Add popover (the "+" on a bar) ────────────────────────────────────────
  const [add, setAdd] = useState<{ anchorEl: HTMLElement; task: TaskWithBookings; bookingKey: string; assignees: TaskAssignee[] } | null>(null);
  // ── Remove confirm + skill/overbook escalation dialog ─────────────────────
  const [confirm, setConfirm] = useState<ConfirmDialogState>(CONFIRM_INITIAL);
  const [dialogTask, setDialogTask] = useState<TaskWithBookings | null>(null);
  const [dialogPreselect, setDialogPreselect] = useState<AssignableUser | null>(null);
  const [dialogBookingKey, setDialogBookingKey] = useState<string | null>(null);

  const reload = useCallback((taskKey: string) => {
    onAssignmentsChanged(taskKey);
    onAllocationChanged?.();
  }, [onAssignmentsChanged, onAllocationChanged]);

  // P-17a: a partial bulk failure used to vanish (per-row .catch(() => null)) —
  // now any failed row raises one red snackbar; reload() still reconciles the grid.
  const [bulkError, setBulkError] = useState<string | null>(null);

  const removeBulk = useCallback(async (taskKey: string, rowKeys: string[]) => {
    const results = await Promise.all(
      rowKeys.map((k) => deleteEmployeeAssignment(k).then(() => true).catch(() => false)),
    );
    if (results.includes(false)) {
      setBulkError('Some rows failed to save — the grid was reloaded with actual values.');
    }
    reload(taskKey);
  }, [reload]);

  /** One person on a bar — avatar + first name (click → detail/remove popover). */
  const personChip = (p: RosterPerson, task: TaskWithBookings, bookingKey: string) => (
    <Tooltip key={p.userKey} title={`${p.displayName} — click for options + see them in the capacity grid`} arrow>
      <Box
        onClick={(e) => { flashUser(p.userKey); setEdit({ anchorEl: e.currentTarget, task, bookingKey, person: p }); }}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5, pl: 0.2, pr: 0.6, py: 0.1, borderRadius: '999px',
          minWidth: 0, maxWidth: 200,
          border: `1px ${p.status === 'Committed' ? 'solid' : 'dashed'} ${alpha(p.status === 'Committed' ? theme.schedule.committed : theme.schedule.employeeAlloc, 0.5)}`,
          backgroundColor: alpha(theme.palette.background.paper, 0.85), cursor: 'pointer',
          '&:hover': { backgroundColor: theme.palette.background.paper },
        }}
      >
        <InitialsAvatar displayName={p.displayName} seed={p.userKey} photoUrl={photoByEmail?.get(p.email)} size={23} />
        <Typography variant="caption" noWrap sx={{ fontSize: '0.7rem', fontWeight: 500, color: 'text.primary', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {p.displayName.split(/\s+/)[0]}
        </Typography>
      </Box>
    </Tooltip>
  );

  if (tasks.length === 0 || weekColumns.length === 0) return null;

  const beamLeft = hoveredWeek ? weekColumns.indexOf(hoveredWeek) : -1;

  return (
    <>
      <AssignEmployeesDialog
        open={dialogTask !== null}
        task={dialogTask}
        preselectUser={dialogPreselect}
        onClose={() => { setDialogTask(null); setDialogPreselect(null); setDialogBookingKey(null); }}
        onSaved={() => { if (dialogTask) reload(dialogTask.ScheduleTaskKey); }}
        onAssigneesChanged={() => { if (dialogTask) reload(dialogTask.ScheduleTaskKey); }}
        scenarioKey={scenarioKey ?? undefined}
        machineBookingKey={dialogBookingKey}
      />

      <Box
        onMouseLeave={() => onHoverWeek?.(null)}
        sx={{
          border: `1px solid ${theme.border.color}`,
          borderRadius: `${theme.border.radius}px`,
          backgroundColor: theme.palette.background.paper,
          width: 'fit-content',
          overflow: 'hidden',
        }}
      >
        {tasks.map((task, rowIdx) => {
          const rows = assignmentMap[task.ScheduleTaskKey] ?? [];
          const Icon = iconForTaskType(task);
          const bars = task.Bookings
            .map((b) => ({ b, span: bookingSpan(b, weekColumns) }))
            .filter((x): x is { b: BookingView; span: { startIdx: number; endIdx: number } } => x.span !== null);
          // Sole booking folds legacy null-MachineBookingKey rows in (matches the Board).
          const isSole = bars.length === 1;
          const matched = new Set<string>();
          const barData = bars.map(({ b, span }) => {
            const people = peopleOnBooking(rows, b.MachineBookingKey, isSole);
            people.forEach((p) => p.rowKeys.forEach((k) => matched.add(k)));
            return { b, span, people };
          });
          // Anyone whose rows landed in NO bar (null-booking with ≥2 machines, or orphan) —
          // surface on a "Whole task" strip so the Roster never drops people the Board shows.
          const leftover = groupByUser(rows.filter((r) => !matched.has(r.EmployeeAssignmentKey)));
          const laneCount = barData.length + (leftover.length > 0 ? 1 : 0);
          const trackH = Math.max(1, laneCount) * BAR_ROW_H;
          const rowBg = rowIdx % 2 === 1 ? theme.table.stripeBg : undefined;
          // Lead each bar with the WORK (WO / project / quote), machine → tooltip.
          const workLbl = taskWorkLabel(task);

          return (
            <Box key={task.ScheduleTaskKey} sx={{ display: 'flex', borderTop: rowIdx === 0 ? 'none' : `1px solid ${theme.border.color}`, backgroundColor: rowBg }}>
              {/* Gutter — icon + task name (sticky, frozen divider) */}
              <Box
                sx={{
                  position: 'sticky', left: 0, zIndex: 2, width: RAIL_GUTTER_W, flexShrink: 0,
                  backgroundColor: rowBg ?? theme.palette.background.paper,
                  boxShadow: `inset -1px 0 0 0 ${theme.border.color}, 6px 0 6px -6px ${alpha(theme.palette.text.primary, 0.18)}`,
                  display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
                }}
              >
                <Box sx={{ flexShrink: 0, width: 26, height: 26, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: alpha(theme.schedule.machineTime, 0.12), color: theme.schedule.machineTime }}>
                  <Icon sx={{ fontSize: 16 }} />
                </Box>
                <Typography variant="body2" fontWeight={700} noWrap sx={{ fontSize: '0.82rem' }}>
                  {displayTaskName(task.Name, task.TaskTypeName)}
                </Typography>
              </Box>

              {/* Track — week-positioned booking bars */}
              <Box sx={{ position: 'relative', width: trackW, height: trackH }}>
                {/* Week hover zones + column separators + beam */}
                {weekColumns.map((ws, i) => (
                  <Box
                    key={ws}
                    onMouseEnter={() => onHoverWeek?.(ws)}
                    sx={{
                      position: 'absolute', top: 0, bottom: 0, left: i * RAIL_WEEK_W, width: RAIL_WEEK_W,
                      borderRight: i < weekColumns.length - 1 ? `1px solid ${alpha(theme.border.color, 0.6)}` : 'none',
                      backgroundColor: i === beamLeft ? alpha(theme.schedule.selection, 0.06) : 'transparent',
                      ...(i === beamLeft ? { borderLeft: `2px solid ${theme.schedule.selection}`, borderRight: `2px solid ${theme.schedule.selection}` } : {}),
                    }}
                  />
                ))}

                {barData.map(({ b, span, people }, bi) => {
                  const left = span.startIdx * RAIL_WEEK_W + 4;
                  const width = (span.endIdx - span.startIdx + 1) * RAIL_WEEK_W - 8;
                  const tentative = b.Status === 'Tentative';
                  const single = b.StartDate === b.EndDate;
                  const dates = single ? fmtShortDate(b.StartDate) : `${fmtShortDate(b.StartDate)}–${fmtShortDate(b.EndDate)}`;
                  const taskAssignees = rows.filter((r) => {
                    const mbk = r.MachineBookingKey ?? null;
                    return mbk === b.MachineBookingKey || (isSole && mbk === null);
                  });
                  return (
                    <Box
                      key={b.MachineBookingKey}
                      sx={{
                        position: 'absolute', top: bi * BAR_ROW_H + 5, left, width, minHeight: BAR_ROW_H - 10,
                        border: `1px solid ${alpha(theme.schedule.machineTime, 0.3)}`,
                        borderLeft: `3px ${tentative ? 'dashed' : 'solid'} ${theme.schedule.machineTime}`,
                        borderRadius: '6px', backgroundColor: alpha(theme.schedule.machineTime, tentative ? 0.06 : 0.1),
                        px: 0.75, py: 0.4, display: 'flex', flexDirection: 'column', gap: 0.25, overflow: 'hidden',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                        <Tooltip title={`${workLbl ? `${workLbl} · ` : ''}${b.MachineName ?? b.MachineCode ?? 'Machine'} · ${dates}`} placement="top" enterDelay={400}>
                          <Typography variant="caption" noWrap sx={{ fontSize: '0.72rem', fontWeight: 700, color: theme.schedule.machineTime, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {(workLbl ?? b.MachineName ?? b.MachineCode ?? 'Machine')} · {dates}
                          </Typography>
                        </Tooltip>
                        {/* The Sa–Fr strip can only represent ONE week, but a Roster bar can span
                            several week COLUMNS (its position already shows that span). So only show
                            the strip for a single-week booking; for a multi-week one it would merge
                            different weeks' weekdays into one scrambled strip (Gigi, 2026-06-25). */}
                        {span.startIdx === span.endIdx && (
                          <Box sx={{ width: 108, flexShrink: 0 }}>
                            <BookingWeekStrip rangeStart={b.StartDate} rangeEnd={b.EndDate} holidays={holidays} runMode={task.RunMode ?? null} />
                          </Box>
                        )}
                        <Box sx={{ flex: 1 }} />
                        <Tooltip title="Add an employee">
                          <IconButton
                            size="small"
                            onClick={(e) => setAdd({ anchorEl: e.currentTarget, task, bookingKey: b.MachineBookingKey, assignees: taskAssignees })}
                            sx={{ flexShrink: 0, p: 0.2, color: theme.schedule.employeeAlloc }}
                          >
                            <AddIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                        {people.length === 0 ? (
                          <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 700, color: theme.schedule.maintenance }}>Unstaffed — add someone</Typography>
                        ) : people.map((p) => personChip(p, task, b.MachineBookingKey))}
                      </Box>
                    </Box>
                  );
                })}
                {leftover.length > 0 && (
                  <Box
                    sx={{
                      position: 'absolute', top: barData.length * BAR_ROW_H + 5, left: 4, width: trackW - 8, minHeight: BAR_ROW_H - 10,
                      border: `1px dashed ${alpha(theme.palette.text.disabled, 0.4)}`, borderRadius: '6px',
                      backgroundColor: alpha(theme.palette.text.disabled, 0.04), px: 0.75, py: 0.4,
                      display: 'flex', flexDirection: 'column', gap: 0.25, overflow: 'hidden',
                    }}
                  >
                    <Typography variant="caption" sx={{ fontSize: '0.62rem', color: 'text.disabled', fontStyle: 'italic' }}>
                      Whole task (not machine-specific)
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                      {leftover.map((p) => personChip(p, task, ''))}
                    </Box>
                  </Box>
                )}
                {barData.length === 0 && leftover.length === 0 && (
                  <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled', fontSize: '0.85rem' }}>—</Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Person popover — presence detail + remove (Wave 3: the % drag-bar is gone). */}
      <Popover
        open={Boolean(edit)}
        anchorEl={edit?.anchorEl}
        onClose={() => setEdit(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        disableScrollLock
        PaperProps={{ sx: { p: 1.25, width: 260, borderRadius: '8px', border: `1px solid ${theme.border.color}` } }}
      >
        {edit && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <InitialsAvatar displayName={edit.person.displayName} seed={edit.person.userKey} photoUrl={photoByEmail?.get(edit.person.email)} size={28} />
              <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.82rem', flex: 1, minWidth: 0 }}>{edit.person.displayName}</Typography>
              <Tooltip title="Remove from this test">
                <IconButton
                  size="small"
                  onClick={() => {
                    const { task, person } = edit;
                    setConfirm({
                      open: true, title: 'Remove employee?', message: `Remove ${person.displayName} from this test?`,
                      confirmLabel: 'Remove', confirmColor: 'error',
                      onConfirm: () => { void removeBulk(task.ScheduleTaskKey, person.rowKeys); },
                    });
                    setEdit(null);
                  }}
                  sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.66rem' }}>
              Staffed on every week of this booking — busy (full day) on the days it covers.
            </Typography>
          </Box>
        )}
      </Popover>

      {/* Add popover — the inline picker (adds across the booking's weeks) */}
      <Popover
        open={Boolean(add)}
        anchorEl={add?.anchorEl}
        onClose={() => setAdd(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableScrollLock
        PaperProps={{ sx: { p: 1, width: 320, borderRadius: '8px', border: `1px solid ${theme.border.color}` } }}
      >
        {add && (
          <InlineAssigneePicker
            task={add.task}
            assignees={add.assignees}
            machineBookingKey={add.bookingKey}
            onAssigned={() => { reload(add.task.ScheduleTaskKey); setAdd(null); }}
            onUnassigned={() => reload(add.task.ScheduleTaskKey)}
            onEscalateToDialog={(t, u, mbk) => { setDialogTask(t); setDialogPreselect(u ?? null); setDialogBookingKey(mbk ?? null); setAdd(null); }}
            scenarioKey={scenarioKey ?? undefined}
          />
        )}
      </Popover>

      <ConfirmDialog state={confirm} onClose={() => setConfirm(CONFIRM_INITIAL)} />

      {/* P-17a: partial bulk remove failures are no longer silent. */}
      <Snackbar open={Boolean(bulkError)} autoHideDuration={6000} onClose={() => setBulkError(null)}>
        <Alert severity="error" onClose={() => setBulkError(null)}>{bulkError}</Alert>
      </Snackbar>
    </>
  );
};
